import { useMemo, useState } from 'react';
import { ICS2CodelistsMap } from 'store/codelists/code';
import useDebouncedValue from 'hooks/useDebouncedValue';
import useIcs2CodelistOptions from './useIcs2CodelistOptions';
import { useCodelistsLoadingContext } from './CodelistsLoadingContext';
import type { FormSelectProps, GroupSelectOption, SelectOption, SelectSearchOption } from './DeclarationSelect';

interface UseSelectOptionsProps {
    codelistProps: FormSelectProps['codelistProps'];
    selectOptions: Readonly<SelectOption[]> | Readonly<GroupSelectOption> | undefined;
    fieldValue: any;
}

// Resolves the two ways a DeclarationSelect can get its options: preloaded via `selectOptions`,
// or fetched on demand from an ICS2 codelist via `codelistProps`. Also resolves the loading state
// and the antd Select props (filterOption/onSearch/searchValue/onDropdownVisibleChange) that differ
// between the two modes, so callers don't need to branch on the mode themselves.
const useSelectOptions = ({ codelistProps, selectOptions, fieldValue }: UseSelectOptionsProps) => {
    const codelistName = codelistProps?.codelistName;
    const declarationType = codelistProps?.declarationType;
    const countryCode = codelistProps?.countryCode;
    const isCodelistMode = codelistName != null && declarationType != null;

    // CL244 (UN/LOCODE) is too large to load upfront — options are fetched server-side as the user types.
    const isServerFilteredCodelist = useMemo(
        () => isCodelistMode && codelistName === ICS2CodelistsMap.CL244,
        [codelistName, isCodelistMode]
    );

    // Tracks the user's current search input; undefined means the dropdown hasn't been opened yet.
    const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
    const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

    // When the field has a pre-filled value (e.g. on form load), we pass it as a seedQuery so the
    // hook can resolve its label without waiting for the user to open the dropdown.
    const seedQuery = useMemo(
        () => (isServerFilteredCodelist && typeof fieldValue === 'string' && fieldValue ? fieldValue : undefined),
        [fieldValue, isServerFilteredCodelist]
    );

    const {
        codelistOptions,
        loading: ics2Loading,
        fetchCodelist,
    } = useIcs2CodelistOptions({
        declarationType,
        codelistName,
        // Only pass a query for server-filtered codelists; others filter client-side from a full list.
        query: isServerFilteredCodelist ? debouncedSearchQuery : undefined,
        fetchOnMount: isCodelistMode && (Boolean(fieldValue) || Boolean(fieldValue?.[0])),
        seedQuery,
        countryCode,
    });

    const contextCodelistsLoading = useCodelistsLoadingContext();
    const loading = isCodelistMode ? ics2Loading : contextCodelistsLoading && selectOptions === undefined;

    const options = useMemo(
        () => (isCodelistMode ? codelistOptions : selectOptions),
        [codelistOptions, isCodelistMode, selectOptions]
    );

    const searchProps = useMemo(
        () => ({
            filterOption: isServerFilteredCodelist
                ? (false as const)
                : (input: string, option: SelectSearchOption) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                      option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0,
            onSearch: isServerFilteredCodelist ? (value: string) => setSearchQuery(value) : undefined,
            searchValue: isServerFilteredCodelist ? searchQuery ?? '' : undefined,
            onDropdownVisibleChange: (open: boolean) => {
                if (!open || !isCodelistMode) return;

                if (isServerFilteredCodelist) {
                    // Reset search to empty so the dropdown shows a broad default
                    // result set. The seed option (pre-filled value) is always
                    // merged in by the hook, so it remains visible.
                    setSearchQuery('');
                } else {
                    void fetchCodelist();
                }
            },
        }),
        [isServerFilteredCodelist, isCodelistMode, searchQuery, fetchCodelist]
    );

    return { options, loading, searchProps };
};

export default useSelectOptions;
